import { gsap } from 'gsap'
import WebFont from 'webfontloader'

import { removeElement, toKebabCase, getCubicCurve } from 'js/utils/Utils'

import Schedule from 'js/utils/Schedule'

import Common from 'js/_core/Common'

import LoaderProgress from 'js/modules/_base/helper/resourceLoader/LoaderProgress'
import WebglImagesLoader from 'js/modules/_base/helper/resourceLoader/image/WebglImagesLoader'
import OpeningLoader from 'js/modules/_base/helper/OpeningLoader'

const defaults = {
  $name: {
    wrapper: '#App',
    cover: '#CoverSite',
    coverInner: '#CoverSite .cover_inner',
    spinner: '#CoverSiteSpinner',
    content: '[data-page]',
  },
  className: {
    page: 'page-',
  },
  fontCustomOptions: {
    families: [''],
    urls: ['/assets/fonts/fonts.css'],
  },
  progressEase: getCubicCurve(0.4, 0.03, 0.23, 1),
}

export default class Startup {
  run(prev, next, $newContent, params, controllerManager, nextTask) {
    const schedule = new Schedule()

    // html elements
    const $body = document.body
    const $app = $body.querySelector(defaults.$name.wrapper)
    const $cover = $app.querySelector(defaults.$name.cover)
    const $coverInner = $app.querySelector(defaults.$name.coverInner)
    const $content = $app.querySelector(defaults.$name.content)
    const $spinner = $app.querySelector(defaults.$name.spinner)
    const $logo = $app.querySelector('.cover_logo')
    const openingLoader = new OpeningLoader()
    let useLoadingAnimation = openingLoader.shouldRun()
    // useLoadingAnimation = false
    let spinnerTween = null

    // id
    const id = $content.getAttribute('data-page') || $content.getAttribute('id')

    // other

    const loaderProgress = new LoaderProgress()
    const easedProgress = { value: 0 }
    const resourceLoaderOptions = {
      $content: document.body,
      isMobileLayout: Common.detection.isMobileLayout,
      useWebgl: Common.detection.useWebgl,
      useInlineVideo: !Common.detection.isMobileLayout,
    }

    // const fontLoaderOptions = {
    //   custom: defaults.fontCustomOptions,
    //   timeout: 3000,
    //   async: true,
    // }

    // Initialize styles
    const initStyle = () => {
      if (useLoadingAnimation) {
        if ($cover) {
          $cover.style.display = 'block'
          $cover.style.opacity = '1'
        }
      } else {
        if ($coverInner) {
          $coverInner.style.display = 'none'
        }
        if ($cover) {
          $cover.style.display = 'block'
          $cover.style.backgroundColor = '#f1f1f1'
          $cover.style.opacity = '1'
        }
      }

      if ($app) $app.style.opacity = ''
    }

    // 実行
    return () => {
      schedule.add((resolve) => {
        $body.classList.add(defaults.className.page + toKebabCase(id))
        $body.classList.add('page-ready')
        controllerManager.add(id, $content)
        initStyle()
        resolve()
      })

      schedule.add((resolve) => {
        controllerManager.use('current').willLoad()
        controllerManager.use('current').beforeEnter()

        // font loader
        // const fontLoad = async () => {
        //   await new Promise((resolve) => {
        //     WebFont.load(
        //       deepMerge(fontLoaderOptions, {
        //         active: resolve,
        //         inactive: resolve,
        //       })
        //     )
        //   })
        // }
        // loaderProgress.add(fontLoad)

        // images loader
        const defaultImagesLoader = new WebglImagesLoader(resourceLoaderOptions)
        defaultImagesLoader.init()
        defaultImagesLoader.imageInfoList.forEach((imageInfoListItem) => {
          loaderProgress.add(
            defaultImagesLoader.load.bind(
              defaultImagesLoader,
              imageInfoListItem,
              () => {
                const src = imageInfoListItem.src
                const $target = imageInfoListItem.$target
                const isGl = imageInfoListItem.drawType === 'gl'
                const data = $target.dataset
                const meshName = data['glItem']

                if (isGl) {
                  $target.dataset['glImg'] = src
                  switch (meshName) {
                    case 'bg-m': {
                      Common.textureList['bgM'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'bg-l': {
                      Common.textureList['bgL'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'bg-xl': {
                      Common.textureList['bgXL'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'slideshow-a-item': {
                      Common.textureList['slideshowAItem'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'slideshow-b-item': {
                      Common.textureList['slideshowBItem'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'list-thumb-a': {
                      Common.textureList['listThumbA'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'list-thumb-b': {
                      Common.textureList['listThumbB'].push(imageInfoListItem.texture)
                      break
                    }
                    case 'banner-a': {
                      Common.textureList['bannerA'].push(imageInfoListItem.texture)
                      break
                    }

                    default: {
                      break
                    }
                  }

                  // $target.style.backgroundImage = `url('${src}')`
                } else {
                  if ($target && src && src.length) {
                    $target.style.backgroundImage = `url('${src}')`
                    const useAspectRatio = $target.dataset['useAspectRatio'] !== undefined && $target.dataset['useAspectRatio'] !== null

                    if (useAspectRatio) {
                      requestAnimationFrame(() => {
                        $target.style.aspectRatio = `${imageInfoListItem.image.naturalWidth} / ${imageInfoListItem.image.naturalHeight}`
                      })
                    }
                  }
                }
              },
              'defaultImagesLoader'
            )
          )
        })

        if (useLoadingAnimation) {
          loaderProgress.add(() => {
            return new Promise((resolve) => {
              if ($spinner) {
                requestAnimationFrame(() => {
                  spinnerTween = gsap.to($spinner, {
                    rotation: 360,
                    force3D: false,
                    ease: 'none',
                    duration: 0.333,
                    repeat: -1,
                    paused: true, // 最初は停止した状態
                  })

                  gsap.to($spinner, {
                    opacity: 1,
                    ease: 'none',
                    duration: 0.5,
                    delay: 0.15,
                  })

                  requestAnimationFrame(() => {
                    spinnerTween.play()
                  })
                })
              }

              if ($logo) {
                gsap.fromTo(
                  $logo,
                  {
                    scale: 1.15,
                  },
                  {
                    scale: 1,
                    ease: 'expo.out',
                    duration: 1.4,
                    delay: 0.15,
                    onComplete: () => {
                      resolve()
                    },
                  }
                )

                gsap.to($logo, {
                  opacity: 1,
                  ease: 'none',
                  duration: 0.5,
                  delay: 0.15,
                })
              } else {
                resolve()
              }
            })
          })
        }

        resolve()
      })

      schedule.add((resolve) => {
        let loaderProgressAnimationInfo = {
          duration: 1,
          completeAnimationDelay: 0.1,
          updateFunc: () => {},
        }

        if (useLoadingAnimation) {
          loaderProgressAnimationInfo = {
            duration: 0,
            completeAnimationDelay: 0.05,
            updateFunc: () => {},
          }
        }

        loaderProgress.init({
          onUpdateProgressAnimation: (progress, onCompleteProgressAnimation) => {
            gsap.killTweensOf(easedProgress, 'value')

            if (progress !== 1) {
              gsap.to(easedProgress, {
                value: progress,
                ease: defaults.progressEase,
                duration: loaderProgressAnimationInfo.duration,
                onUpdate: loaderProgressAnimationInfo.updateFunc.bind(this),
              })
            } else {
              requestAnimationFrame(() => {
                if (useLoadingAnimation) {
                  gsap.to($spinner, {
                    opacity: 0,
                    ease: 'quad.inOut',
                    delay: 0.5,
                    onComplete: () => {
                      if (spinnerTween) {
                        spinnerTween.kill()
                        spinnerTween = null
                      }
                    },
                  })
                  // gsap.to($spinner, {
                  //   scale: 1.4,
                  //   ease: 'power3.out',
                  //   duration: 1,
                  //   delay: 0.5,
                  // })

                  gsap.to(easedProgress, {
                    value: 1,
                    ease: defaults.progressEase,
                    duration: loaderProgressAnimationInfo.duration,
                    onUpdate: loaderProgressAnimationInfo.updateFunc.bind(this),
                    onComplete: () => {
                      requestAnimationFrame(() => {
                        controllerManager.use('current').enter()
                        controllerManager.use('current').didLoad()

                        requestAnimationFrame(() => {
                          onCompleteProgressAnimation()
                        })
                      })
                    },
                  })
                } else {
                  gsap.set(easedProgress, {
                    value: 1,
                    onUpdate: loaderProgressAnimationInfo.updateFunc.bind(this),
                    onComplete: () => {
                      requestAnimationFrame(() => {
                        controllerManager.use('current').enter()
                        controllerManager.use('current').didLoad()

                        requestAnimationFrame(() => {
                          onCompleteProgressAnimation()
                        })
                      })
                    },
                  })
                }
              })
            }
          },
          onCompleteProgressAnimation: () => {
            openingLoader.markAsDone()
            resolve()
            requestAnimationFrame(() => {
              if (useLoadingAnimation) {
                gsap.to($cover, {
                  opacity: 0,
                  duration: 0.5,
                  delay: 0.3,
                  ease: 'quad.inOut',
                  onComplete: () => {
                    removeElement($cover)
                  },
                })
              } else {
                gsap.to($cover, {
                  opacity: 0,
                  duration: 0.5,
                  delay: 0.3,
                  // ease: 'quad.inOut',
                  onComplete: () => {
                    removeElement($cover)
                  },
                })
              }
            })
          },
        })
        loaderProgress.set()

        if (useLoadingAnimation) {
          gsap.delayedCall(0.5, () => {
            loaderProgress.run()
          })
        } else {
          gsap.delayedCall(0.3, () => {
            loaderProgress.run()
          })
        }
      })

      schedule.done(() => {
        requestAnimationFrame(() => {
          Common.screenLock.unlock()
          nextTask()
        })
      })
    }
  }
}